// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportHistory.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * Report History Component
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import connect from 'share/enhancers/Izenda.Connect';
import uiState from 'logic/Izenda.UiState';
import constants from 'logic/data/Izenda.ReportHistoryConstants';
import convertToDateTimeFormat from 'share/utils/Izenda.ConvertToDateTimeFormat';
import Popup from 'share/components/controls/CommonPopup/Izenda.Popup';
import DataGridSearch from 'share/components/controls/DataGrid/Izenda.DataGridSearch';
import Paginator from 'share/components/controls/DataGrid/helpers/Izenda.Paginator';
import { Table, Column } from 'share/components/controls/DataGrid/Izenda.Table';
import ReportHistoryStore from 'logic/Izenda.ReportHistoryStore';
import routeUtils from 'share/utils/Izenda.RouteUtils';
import AuthorizationValidator from 'share/components/hoc/Izenda.AuthorizationValidator';
import AuthorizationConstants from 'logic/data/Izenda.AuthorizationConstants';
import { ACCESS_RIGHT } from 'logic/data/Izenda.AuthorizationConstants';
import LicenseCheckerConstants from 'logic/data/Izenda.LicenseCheckerConstants';
import { isNil } from 'lodash/fp';

const CopyIcon = AuthorizationValidator()((props: any) => (
  <a onClick={props.onClick}>
    <span title={constants.TOOL_TIP.COPY} className='icon-copy' />
  </a>
));

const DeleteIcon = AuthorizationValidator()((props: any) => (
  <a onClick={props.onClick}>
    <span title={constants.TOOL_TIP.DELETE} className='icon-cancel' />
  </a>
));

const MoveIcon = AuthorizationValidator()((props: any) => {
  return props.activeItem ? (
    <a onClick={props.onClick}>
      <span title={constants.TOOL_TIP.MOVE} className='icon-resize-full-alt' />
    </a>
  ) : null;
});

class ReportHistory extends Component {
  static propTypes: {
    onClose: PropTypes.func,
    reportId: PropTypes.string.isRequired,
    showCopy: PropTypes.func,
    showMove: PropTypes.func
  };

  constructor(props) {
    super(props);
    const { reportId } = props;
    this.store = new ReportHistoryStore();
    reportId && this.store.init(reportId);
  }

  onClosing = () => {
    this.props.onAccept();
  };

  /**
   * Render Text column
   */
  renderTextCell = (historyItem: any, key: string) => (
    <span className='truncate overflow-text' title={historyItem[key]}>
      {historyItem[key]}
    </span>
  );

  /**
   * Render Date Time column
   */
  renderDateTimeCell = (historyItem: any, key: string) => {
    const data = historyItem[key] ? convertToDateTimeFormat(uiState.currentUser.dateFormat, historyItem[key]) : '';

    return (
      <span className='truncate overflow-text' title={data}>
        {data}
      </span>
    );
  };

  /**
   * Render Version column
   */
  renderVersion = (historyItem: any, key: string) => {
    const inAccessable = historyItem.inaccessable;
    if (inAccessable) {
      return (
        <span className='truncate overflow-text' title={historyItem[key]}>
          {historyItem[key]}
        </span>
      );
    } else {
      const props = this.props;
      const viewHistoryUrl = routeUtils.getReportUrl('View', {
        id: historyItem.reportId,
        version: historyItem.version
      });
      const router = props.router;
      const clickVersion = () => {
        props.onAccept();
        router.push(viewHistoryUrl);
      };
      return (
        <a onClick={clickVersion}>
          <span className='truncate overflow-text' title={historyItem[key]}>
            {historyItem[key]}
          </span>
        </a>
      );
    }
  };

  /**
   * Render Action column
   */
  renderAction = (historyItem: any) => {
    const props = this.props;
    const store = this.store;
    const activeItem = historyItem.status === constants.STATUS.ACTIVE;
    const { accessPriority, inaccessable: inAccessable } = historyItem;
    const { isGlobal, authorization } = props;
    const hasReportDesignerLicense = authorization.checkLicense(LicenseCheckerConstants.LICENSE_KEY.ReportDesigner);
    const shouldDisplayIcon = isGlobal ? uiState.currentUser.systemAdmin && isNil(uiState.tenantId) : true;
    const designReportUri = routeUtils.getReportDesignerUrl('ReportDesignerDetail', { reportId: historyItem.reportId });

    const showCopyPopup = () => {
      props.showCopy(historyItem.version);
    };

    const showMovePopup = () => {
      props.showMove(historyItem.version);
    };

    const deleteReport = () => {
      const message = activeItem ? constants.MESSAGES.M01b : constants.MESSAGES.M01a;
      const deleteVersion = activeItem ? null : historyItem.version;

      props
        .confirmFn('Confirmation', {
          description: message
        })
        .then(() => {
          store.deleteReportHistory(historyItem.reportId, deleteVersion);
          // Handle delete the latest version
          if (!deleteVersion) {
            props.onAccept(historyItem.reportId);
          }
          props.deleteHistory && props.deleteHistory();
        });
    };

    const handleEditReport = () => {
      props.onAccept();
      props.router.push(designReportUri);
    };

    return (
      <div>
        {activeItem && hasReportDesignerLicense && accessPriority === ACCESS_RIGHT.FullAccess && (
          <span onClick={handleEditReport} title={constants.TOOL_TIP.DESIGN} className='icon-pencil' />
        )}
        {(accessPriority === ACCESS_RIGHT.FullAccess ||
          accessPriority === ACCESS_RIGHT.QuickEdit ||
          accessPriority === ACCESS_RIGHT.SaveAs) &&
          !inAccessable && (
            <CopyIcon
              onClick={showCopyPopup}
              keys={[AuthorizationConstants.PERMISSION_KEY.ReportsCanCreateNewReportValue]}
            />
          )}
        {accessPriority === ACCESS_RIGHT.FullAccess && shouldDisplayIcon && (
          <MoveIcon
            activeItem={activeItem}
            onClick={showMovePopup}
            keys={[AuthorizationConstants.PERMISSION_KEY.ReportsActionsOverwriteExistingReport]}
          />
        )}
        {accessPriority === ACCESS_RIGHT.FullAccess && shouldDisplayIcon && !inAccessable && (
          <DeleteIcon onClick={deleteReport} keys={[AuthorizationConstants.PERMISSION_KEY.ReportsActionsDel]} />
        )}
      </div>
    );
  };

  /**
   * Render Body
   */
  renderBody = () => {
    const store = this.store;
    const model = store.model;
    const TABLE_DEF = constants.TABLE_DEF;

    return (
      <section className='report-history'>
        <DataGridSearch
          options={constants.CRITERIA_LIST}
          className='history-search'
          searchPropClass='izenda-GridLayout-Col3'
          searchInputClass='izenda-GridLayout-Col5'
          value={model.pageInfo.searchProperty}
          onSearch={store.search.bind(store)}
          onSearchPropertiesChange={store.onSearchPropertiesChange.bind(store)}
        />

        <Table
          dataSource={model.histories}
          onSortChange={store.onSortChange.bind(store)}
          sortInfo={model.pageInfo.colSortDirs}
        >
          <Column {...TABLE_DEF['version']} cell={this.renderVersion} />

          <Column {...TABLE_DEF['status']} cell={this.renderTextCell} />

          <Column {...TABLE_DEF['editedDate']} cell={this.renderDateTimeCell} />

          <Column {...TABLE_DEF['editedBy']} cell={this.renderTextCell} />

          <Column {...TABLE_DEF['action']} cell={this.renderAction} />
        </Table>

        <Paginator
          onChange={store.goToPage.bind(store)}
          onDropdownChange={store.onPageSizeChange.bind(store)}
          totalRecords={model.pageInfo.totalRecords}
          pageSize={model.pageInfo.pageSize}
          pageIndex={model.pageInfo.pageIndex}
        />
      </section>
    );
  };

  render() {
    const { localization } = this.props;
    return (
      <Popup visible onClosing={this.onClosing} autoClose={false} type='none' sizeClass='modal-lg'>
        <Popup.Header title={localization.getText('REPORT_LABEL_REPORT_HISTORY')} />
        <Popup.Body>{this.renderBody()}</Popup.Body>
        <Popup.Footer>
          <Popup.ConfirmationButton key='cancel' type='cancel'>
            {localization.getText('COMMON_LABEL_CANCEL')}
          </Popup.ConfirmationButton>
        </Popup.Footer>
      </Popup>
    );
  }
}

const EnhancedReportHistory = connect()(ReportHistory);
EnhancedReportHistory.$popupName = 'ReportHistory';

export default EnhancedReportHistory;



// WEBPACK FOOTER //
// ./ui/modules/Reports/ReportList/ReportHistory/Izenda.ReportHistory.jsx